'use client';

import React, { useState, useEffect, useRef } from 'react';

interface EmailInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string) => void;
  planName: string;
}

export default function EmailInputModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  planName,
}: EmailInputModalProps) {
  const [email, setEmail] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset and focus when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      // Focus input after a short delay to ensure modal is rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Handle escape key to close modal (same as clicking Cancel)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(email);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
        }}
        onClick={onClose}
      />
      
      {/* Modal - styled similar to browser prompt */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          padding: '20px 24px',
          width: '90%',
          maxWidth: '380px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          zIndex: 9999,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - same text as the original prompt */}
        <div style={{ marginBottom: '16px' }}>
          <p style={{
            fontSize: '14px',
            color: '#333',
            margin: 0,
          }}>
            Enter your email to join the {planName} waitlist:
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 10px',
              fontSize: '14px',
              border: '1px solid #767676',
              borderRadius: '4px',
              outline: 'none',
              boxSizing: 'border-box',
              marginBottom: '16px',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#0066cc';
              e.target.style.boxShadow = '0 0 0 2px rgba(0, 102, 204, 0.2)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#767676';
              e.target.style.boxShadow = 'none';
            }}
          />

          {/* Buttons - same as browser prompt (Cancel / OK) */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '6px 16px',
                fontSize: '14px',
                backgroundColor: '#f0f0f0',
                color: '#333',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '6px 16px',
                fontSize: '14px',
                backgroundColor: '#0066cc',
                color: 'white',
                border: '1px solid #0066cc',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              OK
            </button>
          </div>
        </form>
      </div>
    </>
  );
}